/**
 * The nation menu: a compact dropdown for another nation, opened by holding right-click on its
 * land (a long press on touch) or by clicking its row in the Tab board. Header: territory swatch,
 * name in its colour, where we stand (ALLY · CEASEFIRE 2:13 · AT WAR · NEUTRAL) and one line of what
 * we know; then the actions (`state/diplomacy.ts` decides which and in what order). Disabled rows
 * say why beside the label. ↑ ↓ choose (← → between amounts), Enter acts, Esc or a click elsewhere
 * closes. A destructive row (BREAK …) asks for a second press, red.
 */
import { defaultRow, type MenuChoice, type MenuItem, type NationMenu } from '../state/diplomacy';
import { h, setText } from './dom';

export interface NationMenuHooks {
  /** run a row (and, for SEND CREDITS, the chosen amount) */
  act(item: MenuItem, choice?: MenuChoice): void;
  /** a disabled row or amount was pressed: say why */
  refuse(why: string): void;
}

/** a destructive row waits this long for its confirming press */
const CONFIRM_MS = 5000;

export class NationMenuView {
  readonly el = h('div.hud-nation.panel', { role: 'menu' });
  private model: NationMenu | null = null;
  private row = 0;
  private col = 0;
  /** a destructive row armed for its second press */
  private armed: { id: string; until: number } | null = null;
  private swatch: HTMLCanvasElement | null = null;
  private color = '';
  /** the rows' structure as last built, and the elements refreshed in place */
  private layout = '';
  private parts: { tag: HTMLElement; ctx: HTMLElement; rows: { row: HTMLElement; lbl: HTMLElement; note: HTMLElement; chs: HTMLButtonElement[] }[] } | null = null;
  private offKey = () => {};
  private offPointer = () => {};

  constructor(private hooks: NationMenuHooks) {
    this.el.hidden = true;
  }

  get player(): number | null {
    return this.model?.player ?? null;
  }

  /** Open for a nation at screen point (x, y) inside `root` (kept on screen). */
  open(model: NationMenu, color: string, swatch: HTMLCanvasElement, x: number, y: number, root: HTMLElement): void {
    this.close();
    this.model = model;
    this.color = color;
    this.swatch = swatch;
    this.row = defaultRow(model.items);
    this.col = this.defaultCol(model.items[this.row]);
    this.armed = null;
    this.layout = '';
    this.render();
    this.el.hidden = false;
    const w = this.el.offsetWidth;
    const hh = this.el.offsetHeight;
    this.el.style.left = `${Math.round(Math.max(8, Math.min(x + 6, root.clientWidth - w - 8)))}px`;
    this.el.style.top = `${Math.round(Math.max(8, Math.min(y + 6, root.clientHeight - hh - 8)))}px`;
    const key = (e: KeyboardEvent) => this.key(e);
    window.addEventListener('keydown', key, true);
    this.offKey = () => window.removeEventListener('keydown', key, true);
    // a press anywhere else closes it (and still does what it does there); armed after this event
    const down = (e: PointerEvent) => {
      if (!this.el.contains(e.target as Node)) this.close();
    };
    const t = setTimeout(() => window.addEventListener('pointerdown', down, true), 0);
    this.offPointer = () => {
      clearTimeout(t);
      window.removeEventListener('pointerdown', down, true);
    };
  }

  /** New state for the open nation (countdowns, an offer answered): the selection stays on its row. */
  update(model: NationMenu): void {
    if (!this.model || model.player !== this.model.player) return;
    const id = this.model.items[this.row]?.id;
    this.model = model;
    const k = model.items.findIndex((i) => i.id === id);
    this.row = k >= 0 ? k : Math.min(this.row, model.items.length - 1);
    const choices = model.items[this.row]?.choices;
    if (choices) this.col = Math.min(this.col, choices.length - 1);
    this.render();
  }

  /** Close; true if it was open. */
  close(): boolean {
    const was = this.model !== null;
    this.model = null;
    this.armed = null;
    this.el.hidden = true;
    this.offKey();
    this.offPointer();
    this.offKey = () => {};
    this.offPointer = () => {};
    return was;
  }

  private defaultCol(item: MenuItem | undefined): number {
    // amounts start on the middle one (1,000) when it is affordable
    const ch = item?.choices;
    if (!ch) return 0;
    const mid = Math.floor(ch.length / 2);
    return ch[mid]?.enabled ? mid : Math.max(0, ch.findIndex((c) => c.enabled));
  }

  private key(e: KeyboardEvent): void {
    const m = this.model;
    if (!m) return;
    const n = m.items.length;
    const k = e.key;
    if (k === 'Escape') this.close();
    else if (k === 'ArrowDown' || k === 'ArrowUp') {
      this.row = (this.row + (k === 'ArrowDown' ? 1 : n - 1)) % n;
      this.col = this.defaultCol(m.items[this.row]);
      this.render();
    } else if ((k === 'ArrowLeft' || k === 'ArrowRight') && m.items[this.row]?.choices) {
      const c = m.items[this.row].choices!.length;
      this.col = (this.col + (k === 'ArrowRight' ? 1 : c - 1)) % c;
      this.render();
    } else if (k === 'Enter') {
      const item = m.items[this.row];
      if (item) this.press(item, item.choices?.[this.col]);
    } else return;
    // the menu owns these keys while it is open (the arrows would pan the camera)
    e.preventDefault();
    e.stopImmediatePropagation();
  }

  private press(item: MenuItem, choice?: MenuChoice): void {
    if (item.choices) {
      const c = choice ?? item.choices[this.col];
      if (!c) return;
      if (!c.enabled) this.hooks.refuse(c.why ?? 'NOT AVAILABLE');
      else {
        this.hooks.act(item, c);
        this.close();
      }
      return;
    }
    if (!item.enabled) {
      this.hooks.refuse(item.note ?? 'NOT AVAILABLE');
      return;
    }
    if (item.destructive && !(this.armed?.id === item.id && performance.now() < this.armed.until)) {
      this.armed = { id: item.id, until: performance.now() + CONFIRM_MS };
      this.render();
      return;
    }
    this.hooks.act(item);
    this.close();
  }

  /**
   * Draw the menu. The rows are built only when their set changes (an offer answered, a row enabled):
   * countdowns, notes and the selection are written into the same elements, so a press on a row is
   * never lost to a refresh under the pointer.
   */
  private render(): void {
    const m = this.model;
    if (!m) return;
    if (this.armed && performance.now() >= this.armed.until) this.armed = null;
    const layout = JSON.stringify([m.player, m.items.map((i) => [i.id, i.enabled, i.choices?.map((c) => c.enabled) ?? null])]);
    if (layout !== this.layout) this.build(m, layout);
    const tag = this.parts!.tag;
    setText(tag, m.tag);
    tag.className = `tag ${m.stance}`;
    setText(this.parts!.ctx, m.context);
    // (they betrayed us: the line says so first, in red)
    this.parts!.ctx.classList.toggle('betrayed', m.context.startsWith('BETRAYED YOU'));
    m.items.forEach((item, i) => {
      const r = this.parts!.rows[i];
      const sel = i === this.row;
      const armed = this.armed?.id === item.id;
      r.row.className = `item${sel ? ' sel' : ''}${item.enabled ? '' : ' off'}${item.destructive ? ' destr' : ''}${armed ? ' armed' : ''}`;
      setText(r.lbl, armed ? 'CONFIRM BREAK' : item.label);
      const note = armed ? '' : (item.note ?? '');
      setText(r.note, note);
      r.note.hidden = !note;
      r.chs.forEach((b, j) => (b.className = `ch${sel && j === this.col ? ' sel' : ''}${item.choices?.[j]?.enabled ? '' : ' off'}`));
    });
  }

  private build(m: NationMenu, layout: string): void {
    this.layout = layout;
    const tag = h('span.tag');
    const ctx = h('div.ctx');
    const select = (i: number, j: number | null) => {
      if (this.row === i && (j === null || this.col === j)) return;
      this.row = i;
      this.col = j ?? this.defaultCol(this.model?.items[i]);
      this.render();
    };
    // handlers look the row up at press time: the model is refreshed without rebuilding
    const itemAt = (i: number) => this.model?.items[i];
    const rows = m.items.map((item, i) => {
      const lbl = h('span.lbl');
      const note = h('span.note');
      const row = h('div.item', { role: 'menuitem', 'data-id': item.id, onpointerenter: () => select(i, null) }, lbl);
      const chs = (item.choices ?? []).map((c, j) =>
        h('button.ch', {
          onpointerenter: () => select(i, j),
          onclick: (e: MouseEvent) => {
            e.stopPropagation();
            const it = itemAt(i);
            if (it?.choices?.[j]) this.press(it, it.choices[j]);
          },
        }, c.label),
      );
      if (chs.length) row.append(h('span.choices', {}, ...chs));
      else
        row.onclick = () => {
          const it = itemAt(i);
          if (it) this.press(it);
        };
      row.append(note);
      return { row, lbl, note, chs };
    });
    this.parts = { tag, ctx, rows };
    this.el.replaceChildren(
      h('div.head', {}, this.swatch, h('span.name', { style: { color: this.color } }, m.name), tag),
      ctx,
      h('div.items', {}, ...rows.map((r) => r.row)),
    );
  }
}
